import { useEffect, useState, type InputHTMLAttributes } from "react";
import { formatPct, toPct4 } from "@cx/domain";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange"> & {
  /** Inteiro escalado (gramas, milímetros, pct4...) ou null. */
  value: number | null;
  onChange(v: number | null): void;
  /** Texto → inteiro; lança para valor inválido (as funções de @cx/domain lançam DomainError). */
  parse(text: string): number;
  format(v: number): string;
  /** Unidade mostrada ao lado (kg, cm, %). */
  suffix?: string;
};

/**
 * Campo numérico genérico sem float: o texto vira inteiro pelo `parse` no blur, como o MoneyInput e o QtyInput.
 * O FormPage tira o foco antes do alt+S, então o valor digitado entra no salvamento (FE7).
 */
export function ScaledInput({
  value,
  onChange,
  parse,
  format,
  suffix,
  className = "form-control text-end",
  ...rest
}: Props) {
  const show = (v: number | null) => (v === null ? "" : format(v));
  const [text, setText] = useState(show(value));
  const [invalid, setInvalid] = useState(false);
  // `format` costuma vir inline do pai: o texto só é refeito quando o valor muda.
  useEffect(() => {
    setText(show(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const marked = invalid || rest["aria-invalid"] === "true" || rest["aria-invalid"] === true;
  const input = (
    <input
      {...rest}
      inputMode="decimal"
      className={`${className}${marked ? " is-invalid" : ""}`}
      aria-invalid={marked ? "true" : undefined}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        if (text.trim() === "") {
          setInvalid(false);
          onChange(null);
          return;
        }
        try {
          const v = parse(text);
          setInvalid(false);
          setText(show(v));
          onChange(v);
        } catch {
          setInvalid(true);
        }
      }}
    />
  );
  if (!suffix) return input;
  return (
    <div className="input-group">
      {input}
      <span className="input-group-text">{suffix}</span>
    </div>
  );
}

const pctText = (p: number) => formatPct(p).replace(/%$/, "");

/** Percentual em pct4 (12,5% = 125_000), como o spec pede para valores percentuais. */
export function PercentInput(props: Omit<Props, "parse" | "format" | "suffix">) {
  return <ScaledInput {...props} parse={toPct4} format={pctText} suffix="%" />;
}
